// Minimal portal shell. Header anatomy:
//   left column  → workspace logo (if set) replacing the "Client Portal"
//                  kicker, otherwise the kicker text + client company
//                  name; client company name (their own identity) stays
//                  prominent below regardless.
//   right column → Sign out.
// Two-tab nav (Jobs / History) below. Mobile-first: header collapses
// to a single line, content fills width, nav wraps. Tokens only.

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { LogOut, Briefcase, History, FileText, Receipt, MapPin, Inbox, BarChart2, Sun, Moon, Users, Settings } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { usePortalTheme } from '@/lib/portalTheme'

// Narrow-viewport detector for the icons-only nav pattern. Mirrors the
// matchMedia shape used by lib/useIsMobile but at a different threshold
// (the breakpoint here is "the smallest phones" where 7 labeled chips
// won't fit even with horizontal scroll feeling acceptable). Inline so
// we don't generalize a one-call hook prematurely.
function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 400px)').matches
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 400px)')
    const handler = (e: MediaQueryListEvent) => setNarrow(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return narrow
}

export default function PortalLayout() {
  const navigate = useNavigate()
  const { portalUser, signOut } = useAuth()
  const isNarrow = useIsNarrow()
  const { resolvedTheme, toggle: toggleTheme, setBrandColor } = usePortalTheme()

  // Client company name (the portal user's own company) anchors the
  // identity line of the header — clients see their name on their own
  // portal.
  const { data: company } = useQuery({
    queryKey: ['portal-company', portalUser?.company_id],
    queryFn: async () => {
      if (!portalUser?.company_id) return null
      const { data } = await supabase.from('companies').select('id, name').eq('id', portalUser.company_id).maybeSingle()
      return data
    },
    enabled: !!portalUser?.company_id,
    staleTime: 5 * 60 * 1000,
  })

  // Workspace branding (logo + brand_color). Read via portal_workspace
  // view — security_invoker + portal_read policy gives portal users
  // access to just {id, name, logo_url, brand_color}. brand_color
  // flows up to PortalThemeProvider via setBrandColor (effect below)
  // so the accent family overrides apply on the data-theme wrapper.
  // Graceful absence: when logo_url is null the header falls back to
  // the "Client Portal"
  // kicker text. 5-minute cache: logo URL is stable per workspace.
  const { data: workspace } = useQuery({
    queryKey: ['portal-workspace', portalUser?.workspace_id],
    queryFn: async () => {
      if (!portalUser?.workspace_id) return null
      const { data } = await supabase.from('portal_workspace').select('id, name, logo_url, brand_color').maybeSingle()
      return data as { id: string; name: string; logo_url: string | null; brand_color: string | null } | null
    },
    enabled: !!portalUser?.workspace_id,
    staleTime: 5 * 60 * 1000,
  })

  // Push brand_color up to the provider when it arrives / changes.
  // Provider recomputes derived accent family on theme flips.
  useEffect(() => { setBrandColor(workspace?.brand_color ?? null) }, [workspace?.brand_color, setBrandColor])

  // Awaiting-response quote count for the Quotes nav badge. Mirrors the
  // staff Requests badge shape: 30s poll, head:true count, hides at zero.
  // The portal_quotes view is workspace+company-scoped by the view's
  // security_invoker policy, so the query needs no extra filters beyond
  // the status set. Viewers never see this query — the Quotes route is
  // gated below, and React-Query is fine being keyed off a non-rendered
  // nav item (a wasted ~10ms; not worth a role-gated `enabled` flag).
  const { data: quotesAwaitingCount = 0 } = useQuery({
    queryKey: ['portal-quotes-awaiting-count', portalUser?.company_id],
    enabled: !!portalUser?.company_id && portalUser?.role !== 'viewer',
    refetchInterval: 30_000,
    queryFn: async () => {
      const { count } = await supabase
        .from('portal_quotes')
        .select('id', { count: 'exact', head: true })
        .in('status', ['sent', 'viewed'])
      return count ?? 0
    },
  })

  const handleSignOut = async () => {
    await signOut()
    navigate('/portal/login', { replace: true })
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)', display: 'flex', flexDirection: 'column' }}>
      <header style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border-subtle)', padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {workspace?.logo_url ? (
            // Logo replaces the kicker. Capped at 28px tall, ~120px wide
            // to keep the header visually balanced with the company-name
            // line below. alt = workspace.name so screen readers convey
            // the brand context.
            <img src={workspace.logo_url}
              alt={workspace?.name ?? 'Workspace logo'}
              style={{ display: 'block', height: 28, maxWidth: 120, width: 'auto', objectFit: 'contain', marginBottom: 4 }} />
          ) : (
            <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Client Portal</p>
          )}
          <p style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink-primary)', margin: workspace?.logo_url ? 0 : '2px 0 0', letterSpacing: '-0.02em' }}>
            {company?.name ?? 'Loading…'}
          </p>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {/* Theme toggle: icon-only sun/moon. The icon represents what
              clicking will SWITCH TO (sun = "go light", moon = "go dark")
              — the macOS / iOS convention. */}
          <button type="button" onClick={toggleTheme}
            aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 8, background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', cursor: 'pointer' }}>
            {resolvedTheme === 'dark'
              ? <Sun style={{ width: 14, height: 14 }} />
              : <Moon style={{ width: 14, height: 14 }} />}
          </button>
          <button type="button" onClick={handleSignOut}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <LogOut style={{ width: 13, height: 13 }} />
            Sign out
          </button>
        </div>
      </header>

      {/* Nav at 7 items. Above 400px we render labeled chips with
          horizontal-scroll fallback (the established pattern). At or
          below 400px the nav switches to icons-only with aria-labels —
          each link claims accessible-name from `aria-label` while
          giving up the visible text so all 7 fit without scroll on
          smaller phones (iPhone SE @ 375 etc). */}
      <nav aria-label="Portal navigation"
        style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 4, padding: '8px 12px', overflowX: isNarrow ? 'visible' : 'auto', WebkitOverflowScrolling: 'touch', justifyContent: isNarrow ? 'space-between' : 'flex-start' }}>
        {[
          { to: '/portal/jobs',      label: 'Jobs',      icon: Briefcase },
          { to: '/portal/requests',  label: 'Requests',  icon: Inbox },
          // Money surfaces are non-viewer only (route guards mirror this).
          ...(portalUser?.role !== 'viewer'
            ? [
                { to: '/portal/quotes',   label: 'Quotes',   icon: FileText },
                { to: '/portal/invoices', label: 'Invoices', icon: Receipt },
                { to: '/portal/spend',    label: 'Spend',    icon: BarChart2 },
              ]
            : []),
          { to: '/portal/locations', label: 'Locations', icon: MapPin },
          // Team is admin-only.
          ...(portalUser?.role === 'admin'
            ? [{ to: '/portal/team', label: 'Team', icon: Users }]
            : []),
          { to: '/portal/history',   label: 'History',   icon: History },
          // Settings (all roles). Per-user notification toggles live here;
          // a 9th item in the icons-only nav still fits under 400px
          // (9 × ~34px ≈ 306px on iPhone SE @ 375).
          { to: '/portal/settings',  label: 'Settings',  icon: Settings },
        ].map(({ to, label, icon: Icon }) => {
          // Awaiting-response badge — only on the Quotes nav item.
          // Numeric chip above 400px; narrow viewport ditches the count
          // for a small accent dot so the icons-only row stays tight
          // enough for the smallest phones.
          const showBadge = label === 'Quotes' && quotesAwaitingCount > 0
          return (
          <NavLink key={to} to={to} end
            aria-label={showBadge ? `${label} (${quotesAwaitingCount} awaiting)` : label}
            title={isNarrow ? label : undefined}
            style={({ isActive }) => ({
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: isNarrow ? '8px 9px' : '7px 12px', borderRadius: 7,
              fontSize: 13, fontWeight: 600, textDecoration: 'none',
              background: isActive ? 'var(--accent-dim)' : 'transparent',
              color: isActive ? 'var(--accent-text)' : 'var(--ink-tertiary)',
              border: `1px solid ${isActive ? 'var(--accent-border)' : 'transparent'}`,
              flexShrink: 0,
              position: 'relative',
            })}>
            <Icon style={{ width: isNarrow ? 16 : 13, height: isNarrow ? 16 : 13 }} />
            {!isNarrow && label}
            {showBadge && !isNarrow && (
              <span style={{ marginLeft: 2, padding: '1px 6px', background: 'var(--accent)', color: 'var(--accent-ink)', borderRadius: 10, fontSize: 10, fontWeight: 700, minWidth: 16, textAlign: 'center', lineHeight: 1.4 }}>
                {quotesAwaitingCount > 99 ? '99+' : quotesAwaitingCount}
              </span>
            )}
            {showBadge && isNarrow && (
              <span aria-hidden="true" style={{ position: 'absolute', top: 4, right: 4, width: 7, height: 7, background: 'var(--accent)', borderRadius: '50%' }} />
            )}
          </NavLink>
          )
        })}
      </nav>

      <main style={{ flex: 1, padding: '14px 16px 24px', maxWidth: 800, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        <Outlet />
      </main>
    </div>
  )
}
